import { useState } from 'react'
import type { AssignmentWithDetails } from '../../services/assignmentService'
import { Button } from '../common'
import { ProgramAssignmentCard } from './ProgramAssignmentCard'
import { useAuthStore } from '../../stores'

interface ProgramAssignmentsTabProps {
  assignments: AssignmentWithDetails[]
  onAssignClick: () => void
  onAssignmentUpdate: () => void
}

export function ProgramAssignmentsTab({
  assignments,
  onAssignClick,
  onAssignmentUpdate,
}: ProgramAssignmentsTabProps) {
  const { profile } = useAuthStore()
  const canAssign = profile && profile.subscription_tier !== 'free'

  // Group assignments by status
  const activeAssignments = assignments.filter(
    (a) =>
      a.status === 'active' &&
      (!a.expires_at || new Date(a.expires_at) >= new Date())
  )
  const completedAssignments = assignments.filter((a) => a.status === 'completed')
  const expiredAssignments = assignments.filter(
    (a) =>
      a.status === 'active' &&
      a.expires_at &&
      new Date(a.expires_at) < new Date()
  )
  const cancelledAssignments = assignments.filter((a) => a.status === 'cancelled')

  if (assignments.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-xl">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <h3 className="mt-3 text-lg font-medium text-gray-900">
          No clients assigned yet
        </h3>
        <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
          Assign this program to clients so they can access the exercises and track their progress.
        </p>
        {canAssign && (
          <Button onClick={onAssignClick} className="mt-4">
            Assign to Client
          </Button>
        )}
        {!canAssign && (
          <p className="mt-4 text-sm text-gray-500">
            Upgrade your plan to assign programs to clients.
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with assign button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-gray-600">
          {activeAssignments.length > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              {activeAssignments.length} active
            </span>
          )}
          {completedAssignments.length > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              {completedAssignments.length} completed
            </span>
          )}
          {expiredAssignments.length > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>
              {expiredAssignments.length} expired
            </span>
          )}
        </div>
        {canAssign && (
          <Button onClick={onAssignClick}>
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Assign Client
          </Button>
        )}
      </div>

      {/* Active assignments */}
      {activeAssignments.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
            Active Assignments
          </h3>
          <div className="space-y-4">
            {activeAssignments.map((assignment) => (
              <ProgramAssignmentCard
                key={assignment.id}
                assignment={assignment}
                onUpdate={onAssignmentUpdate}
              />
            ))}
          </div>
        </div>
      )}

      {/* Completed assignments */}
      {completedAssignments.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
            Completed
          </h3>
          <div className="space-y-4">
            {completedAssignments.map((assignment) => (
              <ProgramAssignmentCard
                key={assignment.id}
                assignment={assignment}
                onUpdate={onAssignmentUpdate}
              />
            ))}
          </div>
        </div>
      )}

      {/* Expired assignments */}
      {expiredAssignments.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
            Expired
          </h3>
          <div className="space-y-4">
            {expiredAssignments.map((assignment) => (
              <ProgramAssignmentCard
                key={assignment.id}
                assignment={assignment}
                onUpdate={onAssignmentUpdate}
              />
            ))}
          </div>
        </div>
      )}

      {/* Cancelled assignments (collapsed by default) */}
      {cancelledAssignments.length > 0 && (
        <CancelledSection
          assignments={cancelledAssignments}
          onUpdate={onAssignmentUpdate}
        />
      )}
    </div>
  )
}

function CancelledSection({
  assignments,
  onUpdate,
}: {
  assignments: AssignmentWithDetails[]
  onUpdate: () => void
}) {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="border-t border-gray-200 pt-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
      >
        <svg
          className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
        {assignments.length} cancelled assignment
        {assignments.length !== 1 ? 's' : ''}
      </button>
      {isExpanded && (
        <div className="mt-4 space-y-4">
          {assignments.map((assignment) => (
            <ProgramAssignmentCard
              key={assignment.id}
              assignment={assignment}
              onUpdate={onUpdate}
            />
          ))}
        </div>
      )}
    </div>
  )
}
